"use client"
import '../styles/front-style.scss';
import React, { useState, useRef, useEffect } from 'react';
import Calendar from "../components/Calendar/Calendar";
import { Blocks } from "@/components/Common/Interfaces";
import {formatDateToYYYYMMDD, getDatesForNonWorkingDays} from "@/components/Common/Helpers";
import {workingDaysFrontApiGet} from "@/services/workingDaysApi";
import {holidayDaysFrontApiGet} from "@/services/holidayDaysApi";
import {blocksFrontApiGet} from "@/services/blocksApi";
import Sidebar from "@/components/Sidebar/Sidebar";

export default function Front() {
    const [blocks, setBlocks] = useState<Blocks[]>([]);
    const [selectedDay, setSelectedDay] = useState<Date | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [nonWorkingDays, setNonWorkingDays] = useState<string[]>([]);
    const [workingDays, setWorkingDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
    const [fetchedNonWorkingDays, setFetchedNonWorkingDays] = useState<{ date: string, status: 'working' | 'non-working' }[]>([]);

    const calendarRef = useRef<HTMLDivElement>(null);

    const handleDayClick = (day: Date) => {
        setIsVisible(true);
        setSelectedDay(day);
    };

    /**
     * On mount fetch all of the non-working days, working days and blocks currently set for the user from the database.
     */
    useEffect(() => {
        const fetchNonWorkingDays = async () => {
            try {
                const fetchedDays = await holidayDaysFrontApiGet();
                setFetchedNonWorkingDays(fetchedDays);  // Initialize fetched data
            } catch (error) {
                console.error('Error fetching non-working days:', error);
            }
        };

        const fetchWorkingDays = async () => {
            const fetchedWorkingDays = await workingDaysFrontApiGet();

            if (Array.isArray(fetchedWorkingDays) && fetchedWorkingDays.length === 0) {
                const defaultWorkingDays = [0, 1, 2, 3, 4, 5, 6];
                setWorkingDays(defaultWorkingDays);
            } else {
                setWorkingDays(fetchedWorkingDays);
            }
        };

        const fetchBlocks = async () => {
            try {
                const fetchedBlocks = await blocksFrontApiGet();
                setBlocks(fetchedBlocks);  // Initialize fetched data
            } catch (error) {
                console.error('Error fetching blocks:', error);
            }
        };

        fetchWorkingDays();
        fetchNonWorkingDays();
        fetchBlocks();
    }, []);

    /**
     * Initialize non-working days when the component mounts only update when workingDays changes
     */
    useEffect(() => {
        const autoGeneratedNonWorkingDays: string[] = getDatesForNonWorkingDays(workingDays);

        const mergedNonWorkingDays = fetchedNonWorkingDays.reduce((acc: string[], fetchedDay) => {
            if (fetchedDay.status === 'non-working') {
                // Add explicitly non-working days from the database
                acc.push(fetchedDay.date);
            } else if (fetchedDay.status === 'working') {
                // Remove explicitly marked working days from auto-generated non-working days
                acc = acc.filter(day => day !== fetchedDay.date);
            }
            return acc;
        }, autoGeneratedNonWorkingDays);

        // Remove duplicates and set the final list of non-working days
        const uniqueNonWorkingDays = Array.from(new Set(mergedNonWorkingDays));
        setNonWorkingDays(uniqueNonWorkingDays);

    }, [workingDays, fetchedNonWorkingDays]);

    return (
        <main ref={calendarRef}>
            <div className="main-container">
                <>
                    <Calendar
                        blocks={blocks || []}
                        onDayClick={handleDayClick}
                        workingDays={workingDays}
                        nonWorkingDays={nonWorkingDays}
                    />
                    <Sidebar
                        blocks={blocks || []}
                        selectedDay={selectedDay}
                        isVisible={isVisible}
                        nonWorkingDays={nonWorkingDays}
                    />
                </>
            </div>
        </main>
    );
}
